import { useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores';
import { toast } from '@/components/ui/Toast';
import * as Sentry from '@sentry/react';
import type { User } from '@/types';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { setUser, setLoading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session }, error }) => {
      if (error) {
        Sentry.captureException(error, { tags: { context: 'auth_provider.getSession' } });
        toast.error('Could not load your session. Please try logging in again.');
        setUser(null);
        setLoading(false);
        return;
      }

      if (session?.user) {
        try {
          const user = await fetchOrCreateProfile(session.user.id, session.user.email ?? '', session.user.user_metadata.name);
          Sentry.setUser({ id: user.id, email: user.email });
          setUser(user);
        } catch (err) {
          Sentry.captureException(err, { tags: { context: 'auth_provider.fetchProfile' } });
          toast.error('Could not load your profile. Please try logging in again.');
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    }).catch((err) => {
      Sentry.captureException(err, { tags: { context: 'auth_provider.getSession' } });
      toast.error('Could not load your session. Please try logging in again.');
      setUser(null);
      setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      Sentry.addBreadcrumb({ category: 'auth', message: `Auth event: ${event}`, level: 'info' });
      if (event === 'PASSWORD_RECOVERY') {
        // Redirect to reset password page for password recovery flow
        // Only redirect if not already on the reset password page
        if (location.pathname !== '/auth/reset-password') {
          navigate('/auth/reset-password');
        }
      } else if (event === 'SIGNED_IN' && session?.user) {
        try {
          const user = await fetchOrCreateProfile(
            session.user.id,
            session.user.email ?? '',
            session.user.user_metadata.name || session.user.user_metadata.full_name
          );
          Sentry.setUser({ id: user.id, email: user.email });
          setUser(user);
        } catch (err) {
          Sentry.captureException(err, { tags: { context: 'auth_provider.onSignedIn' } });
          toast.error('Could not load your profile. Please try logging in again.');
          setUser(null);
        }
      } else if (event === 'SIGNED_OUT') {
        Sentry.setUser(null);
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [setUser, setLoading, navigate, location.pathname]);

  return <>{children}</>;
}

async function fetchOrCreateProfile(userId: string, email: string, name?: string): Promise<User> {
  // Try to fetch existing profile
  const { data: existingProfile, error: fetchError } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (existingProfile && !fetchError) {
    return {
      id: existingProfile.id,
      email, // Email comes from auth session, not profiles table
      name: existingProfile.name || email.split('@')[0],
      avatar_url: existingProfile.avatar_url,
      plan: existingProfile.plan || 'free',
      role: existingProfile.role || 'user',
      account_status: existingProfile.account_status || 'active',
      tos_accepted_at: existingProfile.tos_accepted_at,
      onboarded: existingProfile.onboarded ?? false,
      user_type: existingProfile.user_type || undefined,
      created_at: existingProfile.created_at,
    };
  }

  // Profile should be auto-created by database trigger on signup
  // But if it doesn't exist, create it (without email - it's not in the schema)
  const newProfile = {
    id: userId,
    name: name || email.split('@')[0],
  };

  const { error: insertError } = await supabase
    .from('profiles')
    .insert(newProfile);

  if (insertError) {
    Sentry.captureException(new Error(`Profile creation failed: ${insertError.message}`), {
      tags: { context: 'auth_provider.createProfile' },
    });
    throw new Error(`Failed to create profile: ${insertError.message}`);
  }

  return {
    id: userId,
    email,
    name: name || (email ? email.split('@')[0] : 'User'),
    plan: 'free',
    role: 'user',
    onboarded: false,
    created_at: new Date().toISOString(),
  };
}
